import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Divider,
    Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PreviewIcon from '@mui/icons-material/Preview';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import scanApi from '../../services/api';
import ReportPreviewDialog from './ReportPreviewDialog';
import FullscreenIcon from '@mui/icons-material/Fullscreen'; // Add this import
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BugReportIcon from '@mui/icons-material/BugReport';
import SecurityIcon from '@mui/icons-material/Security';
import TimelineIcon from '@mui/icons-material/Timeline';
import ListItemIcon from '@mui/material/ListItemIcon';
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Add this import

const ReportsView = () => {
    const [reports, setReports] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportStates, setReportStates] = useState({});
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(null);
    const [groupedReports, setGroupedReports] = useState({});
    const [selectedTargetUrl, setSelectedTargetUrl] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);

    // Aggiungo l'array dei template consentiti
    const allowedTemplates = [
        'CWE Top 25',
        'ISO 27001',
        'NIST SP 800-53',
        'OWASP Top 10 2013',
        'OWASP Top 10 2017',
        'OWASP Top 10 2021',
        'PCI DSS 3.2',
        'DISA STIG',
        'WASC Threat Classification',
        'HIPAA'
    ];

    // Update strings that reference scanner name
    const strings = {
        title: 'ExploitFinder Scanner Reports',
        generateButton: 'Generate ExploitFinder Report',
        downloadButton: 'Download ExploitFinder Report',
        reportTitle: 'ExploitFinder Scanner Security Report'
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [reportsRes, templatesRes] = await Promise.all([
                scanApi.getReports(),
                scanApi.getReportTemplates()
            ]);
            
            // Group reports by target URL
            const grouped = (reportsRes?.reports || []).reduce((acc, report) => {
                const targetUrl = report.target_url;
                if (!acc[targetUrl]) {
                    acc[targetUrl] = [];
                }
                acc[targetUrl].push(report);
                return acc;
            }, {});

            // Sort reports by date within each group
            Object.keys(grouped).forEach(targetUrl => {
                grouped[targetUrl].sort((a, b) => 
                    new Date(b.generation_date) - new Date(a.generation_date)
                );
            });

            setGroupedReports(grouped);
            setTemplates(templatesRes || []);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to fetch data: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = async (targetUrl) => {
        if (!selectedTemplate) {
            setError('Please select a report template'); 
            return;
        }

        try {
            const latestScan = groupedReports[targetUrl][0];
            // Get selected template name
            const selectedTemplateName = templates.find(t => t.template_id === selectedTemplate)?.name || 'Custom Report';
            
            setReportStates(prev => ({
                ...prev,
                [latestScan.scan_id]: { 
                    generating: true,
                    progress: 0,
                    error: null,
                    status: 'processing',
                    templateName: selectedTemplateName // Add template name to state
                }
            }));

            const result = await scanApi.generateReport(selectedTemplate, latestScan.scan_id);
            
            if (result.status === 'completed' && result.reportId) {
                setReportStates(prev => ({
                    ...prev,
                    [latestScan.scan_id]: {
                        generating: false,
                        progress: 100,
                        status: 'completed',
                        reportId: result.reportId,
                        templateName: selectedTemplateName // Preserve template name
                    }
                }));
                
                // Update reports list with template info
                await fetchData();
                setDialogOpen(false);
                setSelectedReportId(result.reportId);
                setPreviewOpen(true);
            }
        } catch (err) {
            console.error('Report generation error:', err);
            setError(`Report generation failed: ${err.message}`);
        }
    };

    const handleDownloadReport = async (reportId) => {
        try {
            await scanApi.downloadReport(reportId);
        } catch (error) {
            setError('Failed to download report: ' + error.message);
        }
    };

    const handlePreviewReport = (reportId) => {
        setSelectedReportId(reportId);
        setPreviewOpen(true);
    };

    // Add this helper function to determine report icon
    const getReportIcon = (report) => {
        // You can customize this based on your report types
        const icons = {
            'developer': <DescriptionIcon color="primary" />,
            'executive': <AssessmentIcon color="info" />,
            'compliance': <SecurityIcon color="success" />,
            'vulnerability': <BugReportIcon color="warning" />,
            'scan': <TimelineIcon color="secondary" />
        };

        // Default to generic report icon if type not found
        return icons[report.template_type] || <DescriptionIcon color="action" />;
    };

    const truncateUrl = (url, maxLength = 50) => {
        if (!url || url.length <= maxLength) return url;
        const start = url.substring(0, Math.floor(maxLength / 2));
        const end = url.substring(url.length - Math.floor(maxLength / 2));
        return `${start}...${end}`;
    };

    const handleCopyUrl = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
                Security Reports
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {Object.entries(groupedReports).map(([targetUrl, reports]) => (
                <Accordion key={targetUrl} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            width: '100%', 
                            justifyContent: 'space-between',
                            position: 'relative',
                            pr: 5 // Space for copy button
                        }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                maxWidth: 'calc(100% - 120px)' // Leave space for reports count
                            }}>
                                <Tooltip title={targetUrl} placement="top">
                                    <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: 'primary.main'
                                            }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyUrl(targetUrl);
                                        }}
                                    >
                                        {truncateUrl(targetUrl)}
                                    </Typography>
                                </Tooltip>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyUrl(targetUrl);
                                    }}
                                    sx={{
                                        ml: 1,
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                        '.MuiAccordionSummary-root:hover &': {
                                            opacity: 1
                                        }
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {reports.length} reports
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            {reports.map((report) => (
                                <ListItem 
                                    key={`${report.scan_id}-${report.generation_date}`}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                            borderRadius: 1
                                        },
                                        mb: 0.5,
                                        transition: 'all 0.2s',
                                        borderRadius: 1
                                    }}
                                >
                                    <ListItemIcon>
                                        {getReportIcon(report)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    Generated on: {new Date(report.generation_date).toLocaleString('en-GB')}
                                                </Typography>
                                                {(reportStates[report.scan_id]?.templateName || report.template_name) && (
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            backgroundColor: 'primary.main',
                                                            color: 'primary.contrastText',
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    >
                                                        {reportStates[report.scan_id]?.templateName || report.template_name}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary"
                                            >
                                                Scan ID: {report.scan_id}
                                            </Typography>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Tooltip title="Preview Report">
                                            <IconButton 
                                                onClick={() => handlePreviewReport(report.report_id)}
                                                size="small"
                                                sx={{ 
                                                    '&:hover': { 
                                                        backgroundColor: 'primary.light',
                                                        color: 'primary.contrastText'
                                                    }
                                                }}
                                            >
                                                <PreviewIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                startIcon={reportStates[reports[0]?.scan_id]?.generating ? 
                                    <CircularProgress size={20} color="inherit" /> : 
                                    <MoreTimeIcon />
                                }
                                onClick={() => {
                                    setSelectedTargetUrl(targetUrl);
                                    setDialogOpen(true);
                                }}
                                disabled={reportStates[reports[0]?.scan_id]?.generating}
                            >
                                {reportStates[reports[0]?.scan_id]?.generating ? 
                                    'Generating Report...' : 
                                    'Generate New Report'
                                }
                            </Button>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}

            {/* Add Snackbar for copy feedback */}
            <Snackbar
                open={copySuccess}
                autoHideDuration={2000}
                onClose={() => setCopySuccess(false)}
                message="URL copied to clipboard"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            {/* Template Selection Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={() => !Object.values(reportStates).some(s => s.generating) && setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Generate New Report</DialogTitle>
                <DialogContent>
                    {selectedTargetUrl && (
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            Target URL: {selectedTargetUrl}
                        </Typography>
                    )}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Report Template</InputLabel>
                        <Select
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            label="Report Template"
                        >
                            {templates
                                .filter(template => allowedTemplates.includes(template.name))
                                .map((template) => (
                                    <MenuItem key={template.template_id} value={template.template_id}>
                                        {template.name}
                                    </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDialogOpen(false)}
                        disabled={Object.values(reportStates).some(s => s.generating)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleGenerateReport(selectedTargetUrl)}
                        disabled={!selectedTemplate || Object.values(reportStates).some(s => s.generating)}
                        startIcon={Object.values(reportStates).some(s => s.generating) ? 
                            <CircularProgress size={20} color="inherit" /> : 
                            null
                        }
                    >
                        {Object.values(reportStates).some(s => s.generating) ? 
                            'Generating...' : 
                            'Generate Report'
                        }
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Report Preview Dialog */}
            <ReportPreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                reportId={selectedReportId}
                fullScreen
            />
        </Box>
    );
};

export default ReportsView;
